import React, { useState, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { Badge } from "src/components/ui/badge";
import { useGoogleMapLoader } from "src/hooks/map";
import debounce from "lodash.debounce";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem } from "src/components/ui/command";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "src/components/ui/accordion";
import { cn } from "src/lib/utils";
import { Plus, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useMariaData, useUpdateMariaData } from "src/hooks/api/maria";
import { useProfileHasPermission } from "src/hooks/profile";
import { MembershipPermission } from "src/enums/permission";
import { useCreatePackageOrderRequest } from "src/hooks/api/membership";
import { useNavigate } from "react-router-dom";
import { useMariaPackage } from "src/hooks/dashboard";

interface MariaLocationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId?: number;
}

type LocationOption = API.Maria.MariaMarket;

const tempLocations = [
  {
    label: "New York, NY",
    value: "ChIJOwg_06VPwokRYv534QaPC8g",
  },
  {
    label: "San Francisco, CA",
    value: "ChIJIQBpAG2ahYAR_6128GcTUEo",
  },
  {
    label: "Los Angeles, CA",
    value: "ChIJE9on3F3HwoAR9AhGJW_fL-I",
  },
  {
    label: "Chicago, IL",
    value: "ChIJ7cv00DwsDogRAMDACa2m4K8",
  },
  {
    label: "Miami, FL",
    value: "ChIJEcHIDqKw2YgRZU-t3XHylv8",
  },
];

const MariaLocation: React.FC<MariaLocationProps> = ({ open, onOpenChange, packageId }) => {
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [originalLocations, setOriginalLocations] = useState<LocationOption[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [predictions, setPredictions] = useState<LocationOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const showVerifiedFilter = useProfileHasPermission(MembershipPermission.ACCESS_PROPERTY_ARBITRAGE_VERIFIED_LIST);
  const { data: orderData, mutate, status } = useCreatePackageOrderRequest();
  const navigate = useNavigate();
  const { data: mariaData, isLoading: isLoadingMariaData, refetch: refetchMariaData } = useMariaData();
  const { mutate: updateMaria, status: updateMariaStatus, data: updateMariaData, isLoading: isUpdating } = useUpdateMariaData();

  const handleSelect = () => {
    if (!packageId) return;

    mutate({
      id: packageId,
      is_installment: false,
    });
  };

  React.useEffect(() => {
    if (status === "success" && orderData?.data) {
      if (orderData.data.package.area_level === 0) {
        navigate(`/order/checkout/${orderData.data.id}/payment-information/`);
      } else {
        navigate(`/order/checkout/${orderData.data.id}/location/`);
      }
    }
  }, [status, orderData]);

  // Initialize Google Places Autocomplete
  const autocompleteService = useMemo(() => (window.google?.maps?.places ? new google.maps.places.AutocompleteService() : null), []);

  const sessionToken = useMemo(() => (window.google?.maps?.places ? new google.maps.places.AutocompleteSessionToken() : null), []);

  const isMaxLocations = useMemo(() => locations.length >= (mariaData?.max_locations || 0), [locations, mariaData]);

  // Helper function to check if locations have changed
  const hasLocationsChanged = useMemo(() => {
    if (locations.length !== originalLocations.length) return true;

    // Compare each location
    return locations.some((location) => !originalLocations.find((orig) => orig.value === location.value && orig.label === location.label));
  }, [locations, originalLocations]);

  const handleLocationSelect = (location: LocationOption) => {
    if (isMaxLocations) return; // Max 3 locations
    if (!locations.find((loc) => loc.value === location.value)) {
      setLocations([...locations, location]);
    }
    setSearchValue("");
    setPredictions([]);
  };

  const handleRemoveLocation = (locationToRemove: LocationOption) => {
    setLocations(locations.filter((loc) => loc.value !== locationToRemove.value));
  };

  const searchPlaces = useCallback(
    debounce((input: string) => {
      if (!input.trim() || !autocompleteService || !sessionToken) {
        setPredictions([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      autocompleteService
        .getPlacePredictions({
          input,
          sessionToken,
          componentRestrictions: { country: "us" },
          types: ["(cities)"], // Only show cities
        })
        .then((res) => {
          if (res) {
            const options = res.predictions.map((item) => ({
              label: item.description,
              value: item.place_id,
            }));
            setPredictions(options);
          }
          setIsSearching(false);
        })
        .catch(() => {
          setPredictions([]);
          setIsSearching(false);
        });
    }, 300),
    [autocompleteService, sessionToken],
  );

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    searchPlaces(value);
  };

  const handleSubmit = () => {
    if (locations.length === 0) return;

    updateMaria({
      locations,
    });
  };

  React.useEffect(() => {
    if (mariaData) {
      if (mariaData?.markets) {
        setLocations(mariaData.markets);
        setOriginalLocations(mariaData.markets); // Store original locations
      } else {
        setLocations([]);
        setOriginalLocations([]); // Store empty array as original
      }
    }
  }, [mariaData]);

  React.useEffect(() => {
    if (updateMariaStatus === "success" && updateMariaData) {
      refetchMariaData();
      onOpenChange(false);
    }
  }, [updateMariaStatus, updateMariaData]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[768px] p-0 gap-0 md:max-h-[100vh] overflow-y-auto">
        <div className="py-6 space-y-6 max-w-[500px] mx-auto px-3 md:px-0">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <DialogHeader>
                <DialogTitle className="flex flex-row items-center gap-0">
                  <p className="text-2xl md:text-4xl font-semibold tracking-normal mb-0 text-start mt-3">
                    Share your property preferences with <span className="text-primary/90">Maria</span>
                  </p>
                  <div className="w-[100px] h-[100px] flex-shrink-0">
                    <img src="/image/maria-avatar.png" alt="Maria AI Assistant" className="w-full h-full object-cover" />
                  </div>
                </DialogTitle>
              </DialogHeader>
              <DialogDescription className="p-0 text-start text-muted-foreground text-sm leading-5 tracking-normal">
                Select your preferred locations, and <span className="font-bold">Maria.Ai</span> will find sublease-friendly landlords in
                those areas. Over the next 30 days, vetted landlords will appear in your matchmaking section, ready for a warm introduction
              </DialogDescription>
            </div>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <motion.span
                className={`absolute -top-[10px] left-3 z-10 bg-background px-3 text-sm font-medium text-foreground tracking-normal ${isMaxLocations ? "text-muted-foreground" : "text-foreground"}`}
              >
                Location
              </motion.span>

              <motion.div
                animate={{
                  scale: isMaxLocations ? 0.99 : 1,
                  opacity: isMaxLocations ? 0.8 : 1,
                }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              >
                <Command
                  className={cn(
                    "rounded-md border bg-background overflow-visible transition-colors duration-200",
                    isMaxLocations ? "border-muted cursor-not-allowed" : "border-primary",
                  )}
                >
                  <div className="relative flex items-center w-full">
                    <CommandInput
                      placeholder={isMaxLocations ? "Maximum locations reached" : "Search any location"}
                      value={searchValue}
                      onValueChange={handleSearchChange}
                      className="flex-1 w-full pr-24 h-12 focus:ring-0"
                      disabled={isMaxLocations}
                    />
                  </div>

                  {searchValue && !isMaxLocations && (
                    <div className="absolute w-full top-[100%] rounded-b-md border border-t-0 bg-background shadow-md z-[999999]">
                      <CommandEmpty className="py-2 px-4 text-sm text-muted-foreground">
                        {isSearching ? "Searching..." : "No locations found"}
                      </CommandEmpty>
                      <CommandGroup>
                        {predictions.map((prediction) => (
                          <CommandItem
                            key={prediction.value}
                            onSelect={() => handleLocationSelect(prediction)}
                            className="cursor-pointer hover:bg-secondary/30"
                          >
                            {prediction.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </div>
                  )}
                </Command>
              </motion.div>
            </div>

            {/* Selected Locations */}
            {locations.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Selected locations</span>
                  <span className="text-xs">
                    {locations.length}/{mariaData?.max_locations || 0} locations selected
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {locations.map((location) => (
                    <Badge
                      key={location.value}
                      className="rounded-full px-3 py-1.5 cursor-pointer bg-transparent text-foreground hover:!bg-primary/5 active:!bg-primary/10 font-normal group shadow-none"
                      onClick={() => handleRemoveLocation(location)}
                    >
                      {location?.label?.split(",")[0]}
                      <span className="ml-2 text-xs">×</span>
                    </Badge>
                  ))}
                </div>
                <AnimatePresence>
                  {isMaxLocations && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 25,
                      }}
                    >
                      <Accordion type="single" collapsible className="mt-3">
                        <AccordionItem value="upgrade" className="border-0">
                          <motion.div transition={{ type: "spring", stiffness: 400, damping: 25 }}>
                            <AccordionTrigger className="p-3 rounded-sm bg-transparent border-[1px] hover:no-underline [&[data-state=open]]:rounded-b-none">
                              <div className="flex items-center gap-3">
                                <Plus className="h-4 w-4 text-primary flex-shrink-0" />
                                <span className="text-sm font-medium">Need more locations?</span>
                              </div>
                            </AccordionTrigger>
                          </motion.div>
                          <AccordionContent className="p-3 rounded-b-sm bg-transparent border-[1px] border-t-[0px]">
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.1 }}
                              className="space-y-2"
                            >
                              <p className="text-xs text-muted-foreground">
                                Upgrade to unlock up to 5 locations and get priority matching with landlords.
                              </p>
                              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                <Button size="sm" className="bg-primary/10 text-primary w-full hover:bg-primary/5 active:bg-primary/10">
                                  Increase limit to 5 locations: <span className="text-muted-foreground">$200/month</span>
                                </Button>
                              </motion.div>
                            </motion.div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Maria's recommendations */}
            <div>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-1 mb-2">
                <span className="text-sm font-medium">Maria&apos;s recommendations</span>
                {isMaxLocations && <span className="text-xs text-muted-foreground">Upgrade to add more locations</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {tempLocations.map((temp, index) => (
                  <Badge
                    // eslint-disable-next-line react/no-array-index-key
                    key={`LocationBadge-${temp.label}-${index}`}
                    className={cn(
                      "rounded-full px-4 py-1.5 font-normal transition-all duration-200 shadow-sm",
                      isMaxLocations || locations.find((loc) => loc.value === temp.value)
                        ? "cursor-not-allowed bg-muted/60 text-muted-foreground hover:bg-muted"
                        : "cursor-pointer bg-transparent text-foreground hover:!bg-primary/5 active:bg-primary/10 active:scale-95",
                    )}
                    onClick={() => {
                      if (isMaxLocations) return;
                      if (!locations.find((loc) => loc.value === temp.value)) {
                        setLocations([...locations, temp]);
                      }
                    }}
                  >
                    {temp.label.split(",")[0]}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 flex flex-col items-center gap-2">
          {showVerifiedFilter ? (
            <>
              <Button
                className="max-w-[240px] w-full"
                size="lg"
                disabled={locations.length === 0 || isUpdating || !hasLocationsChanged}
                onClick={handleSubmit}
              >
                {isUpdating ? (
                  <div className="flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Submitting...
                  </div>
                ) : (
                  "Submit"
                )}
              </Button>
              {locations.length === 0 ? (
                <span className="text-xs text-muted-foreground">Please select at least one location</span>
              ) : (
                !hasLocationsChanged && <span className="text-xs text-muted-foreground">No changes to save</span>
              )}
            </>
          ) : (
            <div className="w-full max-w-[500px] space-y-3">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="bg-muted/30 rounded-md p-4 border border-dashed"
              >
                <div className="space-y-2 text-start">
                  <p className="text-sm font-medium text-muted-foreground">Upgrade to Advanced to unlock:</p>
                  <ul className="text-sm text-muted-foreground space-y-1.5">
                    <li className="flex items-center justify-start gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      Submit location preferences
                    </li>
                    <li className="flex items-center justify-start gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      Get matched with verified landlords
                    </li>
                    <li className="flex items-center justify-start gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      Priority in matchmaking queue
                    </li>
                  </ul>
                </div>
              </motion.div>
              <Button size="lg" className="w-full" onClick={handleSelect}>
                Upgrade Maria.Ai
              </Button>
              <p className="text-xs text-center text-muted-foreground">Cancel anytime. No commitment required.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MariaLocationWithGoogle: React.FC<MariaLocationProps> = (props) => {
  const { isLoaded } = useGoogleMapLoader();

  if (!isLoaded) {
    return null;
  }

  return <MariaLocation {...props} />;
};

export default MariaLocationWithGoogle;
